import { makeStyles } from '@material-ui/core';
import { byxScheme } from '../theme/tokens';

/** Minimum width of the name column (design v2 table rule). */
export const NAME_MIN_WIDTH = 320;

/**
 * Widths of the one-line columns. With the menu open, the table panel is
 * 835px wide at 1280px; name (320 + padding), owner, type, lifecycle and
 * the actions fit it without horizontal scroll. Longer values get the
 * ellipsis and the tooltip.
 */
export const CELL_WIDTHS = {
  owner: 130,
  type: 84,
  apiType: 80,
  lifecycle: 96,
  parent: 200,
  count: 96,
} as const;

const oneLine = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/**
 * The v2 table, implemented once (AD-4): a panel with a hairline border and
 * the one-line rule. The header look (sentence case, 13px 600, muted-text on
 * surface-2) is a theme rule, shared with every stock table. Put `table` on an
 * element around a core-components `Table` / `CatalogTable`, and the cell
 * classes on what the column renderers return.
 *
 * One-line rule: the first column takes the free width (at least 320px) and
 * may use two lines (title, then description or tags). Every other column
 * keeps a fixed width and one line, cut with an ellipsis; the renderer puts
 * the full value in `title`. No cell breaks inside a word.
 */
export const useByxTableStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    table: {
      '& tbody tr:has(a[href])': { cursor: 'pointer' },
      // The design's table panel: a hairline border instead of the card shadow.
      '& .MuiPaper-root': {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 14,
        overflow: 'hidden',
      },
      // Table chrome at the design's size: a 15px title over a hairline, a
      // 38px rounded filter and quiet 13px column names (not bold uppercase).
      '& .MuiToolbar-root': {
        minHeight: 0,
        padding: theme.spacing(1.5, 2, 1.5, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiToolbar-root h2': { fontSize: 15, fontWeight: 600 },
      '& .MuiToolbar-root .MuiInputBase-root': {
        fontSize: 14,
        borderRadius: 10,
      },
      '& .MuiToolbar-root .MuiInputBase-input': { padding: '10px 0' },
      // Header colour, size, weight and case come from the theme (MuiTableCell
      // head); here only the one-line layout.
      '& thead th': {
        padding: theme.spacing(1.5),
        whiteSpace: 'nowrap',
        // The table writes an equal share as each header's inline width;
        // the free width goes to the name column instead (below).
        width: 'auto !important',
      },
      '& tbody td': { padding: theme.spacing(1.75, 1.5) },
      '& thead th:first-child, & tbody td:first-child': {
        paddingLeft: theme.spacing(2.5),
        // The name column takes whatever the fixed columns leave.
        width: '100% !important',
        minWidth: NAME_MIN_WIDTH,
      },
      // No cell breaks inside a word (the theme's cells use break-word).
      '& th, & td': { wordBreak: 'normal', overflowWrap: 'normal' },
    },
    /** Name cell: at least 320px; its lines shrink to fit, never the table. */
    name: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      minWidth: NAME_MIN_WIDTH,
    },
    nameTitle: {
      ...oneLine,
      fontSize: 14,
      fontWeight: 600,
    },
    description: {
      ...oneLine,
      marginTop: 4,
      fontSize: 13,
      color: scheme.mutedText,
    },
    /** A one-line cell in the muted body style. */
    cell: {
      ...oneLine,
      fontSize: 13,
      color: scheme.mutedText,
    },
    owner: { width: CELL_WIDTHS.owner },
    type: { width: CELL_WIDTHS.type },
    apiType: { width: CELL_WIDTHS.apiType },
    lifecycle: { width: CELL_WIDTHS.lifecycle },
    parent: { width: CELL_WIDTHS.parent },
    count: {
      width: CELL_WIDTHS.count,
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
  };
});
